import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { CaseContactKind, CaseContactSummary } from '../types';

const KINDS: CaseContactKind[] = [
  'client_party',
  'opposing',
  'lawyer_external',
  'financier',
  'expert',
  'other',
];

export function CaseContactModal({
  caseId,
  initial,
  onSaved,
  onClose,
  onDeleted,
}: {
  caseId: string;
  initial?: CaseContactSummary | null;
  onSaved: () => void;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const editing = !!initial;

  const [kind, setKind] = useState<CaseContactKind>(initial?.kind ?? 'opposing');
  const [name, setName] = useState(initial?.name ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = {
        kind,
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
      };
      if (editing && initial) {
        await apiFetch(`/api/cases/${caseId}/contacts/${initial.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch(`/api/cases/${caseId}/contacts`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!editing || !initial) return;
    if (!confirm(t('contact_modal.delete_confirm', { name: initial.name }))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/cases/${caseId}/contacts/${initial.id}`, { method: 'DELETE' });
      onDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete_failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">
                {editing ? t('contact_modal.title_edit') : t('contact_modal.title_new')}
              </div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field">
                <label>{t('contact_modal.field_kind')} <span className="req">*</span></label>
                <select value={kind} onChange={(e) => setKind(e.target.value as CaseContactKind)}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`contact_kinds.${k}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>{t('contact_modal.field_name')} <span className="req">*</span></label>
                <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
              </div>
              <div className="field">
                <label>{t('contact_modal.field_phone')}</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="field">
                <label>{t('contact_modal.field_email')}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('contact_modal.field_notes')}</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? t('common:actions.loading_dots') : t('common:actions.save')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
            {editing && onDeleted && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="btn-danger"
                style={{ marginInlineStart: 'auto' }}
              >
                {t('common:actions.delete')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
